import { MoreHorizontal } from 'lucide-react';
// OAUTH import { AlertTriangle, Loader2, Radio } from 'lucide-react';
import { useDisconnectIntegration, useIntegrations } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
// OAUTH import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { confirmDisconnect } from './disconnect-confirm.store';
// OAUTH import { useIntercomConnect } from '../hooks/use-intercom-connect';
import { useIntercomOwnAppConnect } from '../hooks/use-intercom-own-app-connect';
import { IntercomKimTeammate } from './intercom-kim-teammate';
import { IntercomOwnAppForm } from './intercom-own-app-form';
import type { Integration } from '../types';

interface IntercomContentProps {
	integration: Integration;
}

// OAuth connect is off until Intercom approves Kim's app: uncomment the lines marked OAUTH to bring it back.
// OAUTH const CallbackError = ({ message }: { message: string }) => (
// OAUTH 	<div role='alert' className='flex items-start gap-2 p-3 border rounded-lg text-destructive'>
// OAUTH 		<AlertTriangle className='size-4 shrink-0 mt-0.5' />
// OAUTH 		<span className='text-sm'>{message}</span>
// OAUTH 	</div>
// OAUTH );

interface ConnectedViewProps {
	integration: Integration;
	isDisconnecting: boolean;
	onDisconnect: () => void;
}

const ConnectedView = ({ integration, isDisconnecting, onDisconnect }: ConnectedViewProps) => (
	<div className='flex flex-col gap-4 px-5'>
		<div className='flex items-start justify-between'>
			<div className='flex flex-col gap-0.5'>
				<h3 className='text-sm'>Connected {integration.name} Workspace</h3>
				<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
			</div>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant='ghost' size='icon-sm' aria-label='Manage connection'>
						<MoreHorizontal className='size-4' />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align='end'>
					<DropdownMenuItem variant='destructive' onClick={onDisconnect} disabled={isDisconnecting}>
						{isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>

		{integration.connectedName && (
			<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
				<Label className='text-xs text-muted-foreground'>Workspace ID</Label>
				<span className='text-sm font-medium'>{integration.connectedName}</span>
			</div>
		)}

		<IntercomKimTeammate />
	</div>
);

export const IntercomContent = ({ integration }: IntercomContentProps) => {
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();
	// OAUTH const { isPending, errorMessage, connect } = useIntercomConnect({ refetchIntegrations: refetch });
	const ownApp = useIntercomOwnAppConnect({ refetchIntegrations: refetch });

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<ConnectedView integration={integration} isDisconnecting={disconnectMutation.isPending} onDisconnect={handleDisconnect} />
		);
	}

	return (
		<div className='flex flex-col gap-5 px-5 pb-5'>
			<div className='flex flex-col gap-1'>
				<h3 className='text-sm font-semibold'>Link an {integration.name} Workspace</h3>
				<p className='text-sm text-muted-foreground'>
					Step 1: create an app in your Intercom workspace and paste its credentials below. It takes about five
					minutes. You choose the teammate Kim works as right after.
				</p>
			</div>

			<IntercomOwnAppForm
				webhookUrl={ownApp.webhookUrl}
				webhookUrlFailed={ownApp.webhookUrlFailed}
				isConnecting={ownApp.isConnecting}
				errorMessage={ownApp.errorMessage}
				connect={ownApp.connect}
			/>

			{/* OAUTH
			<Separator />

			<div className='flex flex-col gap-3'>
				<p className='text-xs text-muted-foreground'>
					Already have Kim&apos;s Intercom app available in your workspace? Authorize it instead — no setup needed.
				</p>
				{errorMessage && <CallbackError message={errorMessage} />}
				<Button type='button' variant='outline' className='w-full' disabled={isPending} onClick={connect}>
					{isPending ? <Loader2 className='size-4 animate-spin' /> : <Radio className='size-4' />}
					{isPending ? 'Waiting for Intercom…' : 'Connect with Intercom'}
				</Button>
			</div>
			*/}
		</div>
	);
};
